import { FFmpeg, createFFmpeg } from '@ffmpeg/ffmpeg';

/**
 * Handles FFmpeg operations for audio/video processing
 */
export class FFmpegService {
  private static ffmpeg: FFmpeg | null = null;
  private ffmpegInstance: FFmpeg | null = null;

  /**
   * Initialize FFmpeg with WebAssembly
   */
  static async initFFmpeg(): Promise<FFmpeg> {
    if (this.ffmpeg) {
      return this.ffmpeg;
    }

    try {
      const ffmpeg = createFFmpeg({
        log: false,
        corePath: 'https://unpkg.com/@ffmpeg/core@0.9.0/dist/ffmpeg-core.js',
      });

      await ffmpeg.load();
      this.ffmpeg = ffmpeg;
      return ffmpeg;
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Instance methods for use in classes
   */
  async load(): Promise<void> {
    try {
      this.ffmpegInstance = await FFmpegService.initFFmpeg();
    } catch (error) {
      console.error('Error loading FFmpeg:', error);
      throw error;
    }
  }

  async writeFile(name: string, data: Uint8Array): Promise<void> {
    if (!this.ffmpegInstance) {
      throw new Error('FFmpeg not initialized');
    }
    this.ffmpegInstance.FS('writeFile', name, data);
  }

  async readFile(name: string): Promise<Uint8Array> {
    if (!this.ffmpegInstance) {
      throw new Error('FFmpeg not initialized');
    }
    return this.ffmpegInstance.FS('readFile', name);
  }

  async deleteFile(name: string): Promise<void> {
    if (!this.ffmpegInstance) {
      throw new Error('FFmpeg not initialized');
    }
    this.ffmpegInstance.FS('unlink', name);
  }

  async run(...args: string[]): Promise<void> {
    if (!this.ffmpegInstance) {
      throw new Error('FFmpeg not initialized');
    }
    return this.ffmpegInstance.run(...args);
  }

  async terminate(): Promise<void> {
    if (this.ffmpegInstance) {
      try {
        // No explicit terminate method in FFmpeg, but we can clean up references
        this.ffmpegInstance = null;
      } catch (error) {
        console.error('Error terminating FFmpeg:', error);
      }
    }
  }

  /**
   * Convert video to MP4 with faststart flag for proper seeking/streaming
   */
  static async convertVideoToStreamingMp4(videoBlob: Blob): Promise<Blob> {
    const TIMEOUT_MS = 30000;

    const conversion = (async () => {
      const ffmpeg = await this.initFFmpeg();

      ffmpeg.FS('writeFile', 'input.webm', await fetchFile(videoBlob));

      await ffmpeg.run(
        '-i', 'input.webm',
        '-c:v', 'libx264',
        '-preset', 'fast',
        '-crf', '23',
        '-c:a', 'aac',
        '-b:a', '192k',
        '-movflags', '+faststart',
        'output.mp4'
      );

      const data = ffmpeg.FS('readFile', 'output.mp4');

      ffmpeg.FS('unlink', 'input.webm');
      ffmpeg.FS('unlink', 'output.mp4');

      return new Blob([data.buffer], { type: 'video/mp4' });
    })();

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('MP4 conversion timed out')), TIMEOUT_MS)
    );

    return Promise.race([conversion, timeout]);
  }

  /**
   * Combine video with audio
   */
  static async combineVideoWithAudio(
    videoBlob: Blob,
    audioBlob: Blob,
    outputFormat: string = 'mp4'
  ): Promise<Blob> {
    try {
      const ffmpeg = await this.initFFmpeg();

      // Write input files to FFmpeg virtual filesystem
      ffmpeg.FS('writeFile', 'input_video.webm', await fetchFile(videoBlob));
      ffmpeg.FS('writeFile', 'input_audio.mp3', await fetchFile(audioBlob));

      // Build output file name based on format
      const outputFileName = `output.${outputFormat}`;

      // Run FFmpeg command to combine video with audio
      await ffmpeg.run(
        '-i', 'input_video.webm',
        '-i', 'input_audio.mp3',
        '-c:v', 'copy', // Keep video as is
        '-c:a', 'aac',
        '-b:a', '320k', // Increased from default to 320kbps for better audio quality
        '-strict', 'experimental',
        '-map', '0:v',
        '-map', '1:a',
        '-shortest',
        outputFileName
      );

      // Read the output file
      const data = ffmpeg.FS('readFile', outputFileName);

      // Clean up files
      ffmpeg.FS('unlink', 'input_video.webm');
      ffmpeg.FS('unlink', 'input_audio.mp3');
      ffmpeg.FS('unlink', outputFileName);

      const mimeType = outputFormat === 'mp4' ? 'video/mp4' : 'video/webm';
      return new Blob([data.buffer], { type: mimeType });
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Extract audio from video and convert to MP3
   */
  static async extractAudioFromVideo(videoBlob: Blob): Promise<Blob> {
    try {
      const ffmpeg = await this.initFFmpeg();
      
      // Write input file
      ffmpeg.FS('writeFile', 'input.webm', await fetchFile(videoBlob));
      
      // Run FFmpeg command to extract audio and convert to MP3
      await ffmpeg.run(
        '-i', 'input.webm',
        '-vn',                // No video
        '-acodec', 'libmp3lame', // MP3 codec
        '-q:a', '0',          // Highest quality setting (0-9, lower is better)
        '-b:a', '320k',       // 320kbps bitrate
        'output.mp3'
      );
      
      // Read output file
      const data = ffmpeg.FS('readFile', 'output.mp3');
      
      // Clean up files
      ffmpeg.FS('unlink', 'input.webm');
      ffmpeg.FS('unlink', 'output.mp3');
      
      // Create MP3 blob
      return new Blob([data.buffer], { type: 'audio/mp3' });
    } catch (error) {
      console.error('Audio extraction failed:', error);
      throw error;
    }
  }
  
  /**
   * Convert audio to MP3 format
   */
  static async convertToMp3(audioBlob: Blob): Promise<Blob> {
    try {
      const ffmpeg = await this.initFFmpeg();
      
      // Write input file
      ffmpeg.FS('writeFile', 'input.webm', await fetchFile(audioBlob));
      
      // Run FFmpeg command to convert to MP3
      await ffmpeg.run(
        '-i', 'input.webm',
        '-acodec', 'libmp3lame', // MP3 codec
        '-q:a', '0',          // Highest quality setting (0-9, lower is better)
        '-b:a', '320k',       // 320kbps bitrate
        'output.mp3'
      );
      
      // Read output file
      const data = ffmpeg.FS('readFile', 'output.mp3');
      
      // Clean up files
      ffmpeg.FS('unlink', 'input.webm');
      ffmpeg.FS('unlink', 'output.mp3');
      
      // Create MP3 blob
      return new Blob([data.buffer], { type: 'audio/mp3' });
    } catch (error) {
      console.error('MP3 conversion failed:', error);
      throw error;
    }
  }

  /**
   * Check if FFmpeg is available/loaded
   */
  static isAvailable(): boolean {
    return this.ffmpeg !== null;
  }

  /**
   * Clean up FFmpeg instance
   */
  static terminate(): void {
    if (this.ffmpeg) {
      try {
        this.ffmpeg.exit();
      } catch (error) {
        console.error('Error terminating FFmpeg:', error);
      } finally {
        this.ffmpeg = null;
      }
    }
  }
}

// Helper function to fetch file data
async function fetchFile(file: Blob): Promise<Uint8Array> {
  const data = await file.arrayBuffer();
  return new Uint8Array(data);
}